import React, { useCallback, useMemo } from "react";
import { DownloadIcon, DriveIcon, DropboxIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import DropboxAttachmentIcon from "~/components/svg/DropboxAttachmentIcon.js";
import GoogleDriveAttachmentIcon from "~/components/svg/GoogleDriveAttachmentIcon.js";
import { PROJECT_TEMPLATE_FILE } from "~/pages/ProjectTemplates/utils.js";
import Angie from "~/utils/angie.jsx";
import {
  isGoogleDriveFile as getIsGoogleDriveFile,
  isDropboxFile as getIsDropboxFile,
  isLocalFile,
  isWarehouseFile,
} from "~/utils/files.jsx";
import { prepareThumbnailUrl } from "~/utils/utils";
import DownloadLink from "../component.download_link.jsx";
import { useFileTokenContext } from "../FilePreview/index.jsx";
import { fileType } from "../FilePreview/types.jsx";

export const AttachmentListItem = ({ item, onClick }) => {
  const { preview_token, download_token, thumb_token } = useFileTokenContext();
  const type = item.class === PROJECT_TEMPLATE_FILE ? item.type : item.class;

  const isGoogleDriveFile = useMemo(() => getIsGoogleDriveFile(type), [type]);
  const isDropboxFile = useMemo(() => getIsDropboxFile(type), [type]);

  const isDropboxOrGoogleDriveFile = isGoogleDriveFile || isDropboxFile;

  const isLocalOrWarehouseFile = useMemo(() => isLocalFile(type) || isWarehouseFile(type), [type]);

  const downloadUrl = useMemo(() => {
    if (isLocalOrWarehouseFile) {
      return item.download_url
        .replace("--PREVIEW-TOKEN--", preview_token)
        .replace("--DOWNLOAD-TOKEN--", download_token);
    }

    return undefined;
  }, [isLocalOrWarehouseFile, item.download_url, preview_token, download_token]);

  const handleClick = useCallback(() => {
    if (typeof onClick === "function") {
      onClick(item.id);
    }
  }, [item.id, onClick]);

  const thumbnailUrl = useMemo(
    () => prepareThumbnailUrl(item.thumbnail_url, 500, 500, "crop", thumb_token, download_token),
    [download_token, item.thumbnail_url, thumb_token]
  );

  return (
    <li>
      {isDropboxOrGoogleDriveFile ? (
        <div>
          <div
            onClick={handleClick}
            className="attachment_preview tw-cursor-pointer"
          >
            <div className="attachment_preview_content tw-inline-flex tw-items-center tw-justify-center">
              {isGoogleDriveFile ? <GoogleDriveAttachmentIcon /> : null}
              {isDropboxFile ? <DropboxAttachmentIcon /> : null}
            </div>
          </div>
          <div className="attachment_details tw-cursor-pointer">
            <span className="attachment_name">{item.name}</span>
            {item.sizeValue > 0 ? (
              <span className="attachment_size print_hide">{Angie.functions.format_file_size(item.sizeValue)}</span>
            ) : null}
          </div>
          <a
            href={item.preview_url}
            className="btn icon_only attachment_download print_hide"
            target="_blank"
            rel="noreferrer"
          >
            {isGoogleDriveFile ? (
              <>
                <span className="icon1">
                  <DriveIcon />
                </span>
                <span className="icon2">
                  <DriveIcon />
                </span>
              </>
            ) : null}
            {isDropboxFile ? (
              <>
                <span className="icon1">
                  <DropboxIcon />
                </span>
                <span className="icon2">
                  <DropboxIcon />
                </span>
              </>
            ) : null}
          </a>
        </div>
      ) : null}
      {isLocalOrWarehouseFile ? (
        <div>
          <div
            onClick={handleClick}
            className="attachment_preview attachment_comment tw-cursor-pointer"
          >
            <img
              src={thumbnailUrl}
              className="attachment_preview_image tw-object-cover tw-w-full tw-h-full"
            />
            <div
              className="attachment_preview_content"
              style={{ content: `url(${thumbnailUrl})` }}
            />
          </div>
          <div className="attachment_details tw-cursor-pointer">
            <span className="attachment_name">{item.name}</span>
            {item.sizeValue > 0 ? (
              <span className="attachment_size print_hide">{Angie.functions.format_file_size(item.sizeValue)}</span>
            ) : null}
          </div>
          <DownloadLink
            url={downloadUrl}
            className="btn icon_only attachment_download print_hide"
          >
            <span className="icon1">
              <DownloadIcon />
            </span>
            <span className="icon2">
              <DownloadIcon />
            </span>
          </DownloadLink>
        </div>
      ) : null}
    </li>
  );
};

AttachmentListItem.propTypes = {
  item: fileType.isRequired,
  onClick: PropTypes.func,
};
